import { useState } from 'react';

import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import { ContextFormFieldDate } from '@bambi/component-library/src/components/forms/context/ContextFormFieldDate';
import { ContextFormFieldTime } from '@bambi/component-library/src/components/forms/context/ContextFormFieldTime';
import { upperFirst } from 'lodash-es';
import { DateTime } from 'luxon';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppSelector } from 'src/app/store/hooks';
import { convertClientDateTimeToServerISO } from 'src/common/date/convertClientDateTimeToServerISO';
import {
  TripEventRead,
  useTripsEventManualCreatePartialUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { FormSubmitButton } from 'src/common/FormSubmitButton';
import {
  formatRTKQueryError,
  isRTKQueryError,
} from 'src/common/util/formatRTKQueryError';
import formatServerError from 'src/common/util/serverErrorFormatter';

import {
  editableTripEventEnumToOverrideStatusEnumMap,
  isManualStatusUpdateEvent,
} from './eventTypes';
import { useLatestTripEventsOfType } from './useLatestTripEventsOfType';

interface TripStatusUpdatesEditorItemProps {
  event: TripEventRead;
  disableEditing?: boolean;
}

export function TripStatusUpdatesEditorItem({
  event,
  disableEditing = false,
}: TripStatusUpdatesEditorItemProps) {
  const [isEditing, setIsEditing] = useState(false);

  return isEditing ? (
    <TripStatusUpdateEdit event={event} onCancel={() => setIsEditing(false)} />
  ) : (
    <TripStatusUpdateDisplay
      event={event}
      disableEditing={disableEditing}
      onEdit={() => setIsEditing(true)}
    />
  );
}

interface TripStatusUpdateEditProps {
  event: TripEventRead;
  onCancel: () => void;
}

interface FormValues {
  date: string; // yyyy-MM-dd
  time: string; // HH:mm (24-h browser time input)
}

function TripStatusUpdateEdit({ event, onCancel }: TripStatusUpdateEditProps) {
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [manualTripEventImpl, { isLoading: isSubmitting }] =
    useTripsEventManualCreatePartialUpdateMutation();
  const tripId = useAppSelector(
    (state) => state.trip.tripStatusUpdatesModal.tripId
  );

  const {
    events,
    refetch,
    isFetching: isRefetchingEvents,
  } = useLatestTripEventsOfType(tripId);

  const defaultDateTime = DateTime.fromISO(event.occurred_at);

  const methods = useForm<FormValues>({
    defaultValues: {
      date: defaultDateTime.toISODate(),
      time: defaultDateTime.toFormat('HH:mm'),
    },
  });

  const handleSubmit = async (values: FormValues) => {
    setSubmitError(undefined);
    const overrideStatus =
      editableTripEventEnumToOverrideStatusEnumMap[event.event_type];
    if (!overrideStatus) {
      throw new Error(
        `No override status found for event type ${event.event_type}`
      );
    }

    const newTime = DateTime.fromISO(values.date).set({
      hour: Number(values.time.split(':')[0]),
      minute: Number(values.time.split(':')[1]),
    });

    const validationError: string | undefined = validateNewStatusEvent(
      events,
      event.event_type,
      newTime
    );
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    const newTimeServerISO = convertClientDateTimeToServerISO(newTime);
    try {
      const result = await manualTripEventImpl({
        id: tripId,
        patchedTripManualUpdate: {
          event_type: event.event_type,
          date_time: newTimeServerISO,
        },
      }).unwrap();
      if ('errors' in result) {
        setSubmitError(formatServerError(result));
        return;
      }
      await refetch();
      onCancel();
    } catch (error) {
      if (isRTKQueryError(error)) {
        setSubmitError(formatRTKQueryError(error));
      } else {
        setSubmitError('An unknown error occurred');
      }
    }
  };

  return (
    <li className="flex flex-col gap-1 py-2">
      <div className="font-medium text-gray-900">
        {formatEventLabel(event.event_type)}
      </div>
      {submitError && <FormErrorMessage>{submitError}</FormErrorMessage>}
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmit)}
          className="flex flex-col gap-2 sm:flex-row sm:items-end"
        >
          <ContextFormFieldDate
            fieldPath="date"
            label="Date"
            rules={{ required: 'Required' }}
          />
          <ContextFormFieldTime
            fieldPath="time"
            label="Time"
            rules={{ required: 'Required' }}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary-outline"
              onClick={onCancel}
              className="flex-grow"
            >
              Cancel
            </Button>
            <FormSubmitButton
              buttonName="Save"
              className="flex-grow"
              isSubmitting={isSubmitting || isRefetchingEvents}
            />
          </div>
        </form>
      </FormProvider>
      <UpdatedByFooter event={event} />
    </li>
  );
}

interface TripStatusUpdateDisplayProps {
  event: TripEventRead;
  onEdit: () => void;
  disableEditing?: boolean;
}

function TripStatusUpdateDisplay({
  event,
  onEdit,
  disableEditing,
}: TripStatusUpdateDisplayProps) {
  return (
    <li
      className="flex flex-col border-b border-gray-200 py-2"
      data-testid={`trip-status-update-${event.id}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="font-medium text-gray-900">
            {formatEventLabel(event.event_type)}
          </div>
          <div className="text-sm text-gray-600">
            {DateTime.fromISO(event.occurred_at).toLocaleString(
              DateTime.DATETIME_MED
            )}
          </div>
        </div>
        <div>
          <Button variant="ghost" onClick={onEdit} disabled={disableEditing}>
            Edit
          </Button>
        </div>
      </div>
      <UpdatedByFooter event={event} />
    </li>
  );
}

function UpdatedByFooter({ event }: { event: TripEventRead }) {
  return (
    <div className="text-xs text-gray-500">
      Updated by {event.person_name}
      {isManualStatusUpdateEvent(event) && (
        <Badge color="yellow" label="Manual" />
      )}
    </div>
  );
}

export function formatEventLabel(status: TripEventRead['event_type']): string {
  // This will likely change to a map of event types to labels
  return status.split('_').map(upperFirst).join(' ');
}

function validateNewStatusEvent(
  latestEventsOfEachType: TripEventRead[],
  eventType: TripEventRead['event_type'],
  newTime: DateTime
) {
  if (newTime.diff(DateTime.now()).toMillis() > 0) {
    return 'New time must be in the past.';
  }

  // No other events to conflict with
  if (latestEventsOfEachType.length < 2) {
    return undefined;
  }

  const currentIndex = latestEventsOfEachType.findIndex(
    (event) => event.event_type === eventType
  );
  if (currentIndex === -1) {
    return 'Event not found';
  }

  const eventsBefore = latestEventsOfEachType.slice(0, currentIndex);
  const eventThatShouldBeBefore = eventsBefore.reverse().find((event) => {
    const eventTime = DateTime.fromISO(event.occurred_at);
    return newTime.diff(eventTime).toMillis() <= 0;
  });
  if (eventThatShouldBeBefore) {
    return `New time must be after ${formatEventLabel(
      eventThatShouldBeBefore.event_type
    )} at ${DateTime.fromISO(
      eventThatShouldBeBefore.occurred_at
    ).toLocaleString(DateTime.DATETIME_MED)}`;
  }

  const eventsAfter = latestEventsOfEachType.slice(currentIndex + 1);
  const eventThatShouldBeAfter = eventsAfter.find((event) => {
    const eventTime = DateTime.fromISO(event.occurred_at);
    return newTime.diff(eventTime).toMillis() >= 0;
  });
  if (eventThatShouldBeAfter) {
    return `New time must be before ${formatEventLabel(
      eventThatShouldBeAfter.event_type
    )} at ${DateTime.fromISO(eventThatShouldBeAfter.occurred_at).toLocaleString(
      DateTime.DATETIME_MED
    )}`;
  }

  return undefined;
}
